import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { FormControlLabel, Checkbox } from '@material-ui/core';
import Switch from '@material-ui/core/Switch';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Formik } from 'formik';
import * as yup from 'yup';
import QRCode from 'react-qr-code';
import ReactToPrint from 'react-to-print';
import { useDispatch, useSelector } from 'react-redux';
import { getErrorMessage } from '../utils';
import { register } from '../store/actions/auth';
import { ROOT } from '../constants';

const useStyles = makeStyles(() => ({
  registerWrapper: {
    height: '100vh',
    width: '100vw',
    backgroundColor: 'dimgray',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  cardWrapper: {
    minWidth: 630,
    width: '50%',
    boxSizing: 'border-box',
    padding: '5em',
    margin: 'auto',
    top: '10%',
  },
  registerButton: {
    marginTop: 16,
  },
  error: {
    color: 'red',
  },
  row: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  nameFieldContainer: {
    width: '48%',
  },
  pinContainer: {
    width: '48%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  checkbox: {
    fontSize: 12,
  },
}));

const Register = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const serverErrors = useSelector((state) => state.auth.error);
  const token = useSelector((state) => state.auth.token);
  const [displayError, setDisplayError] = useState([]);
  const history = useHistory();
  const printRef = useRef();
  const [generatedPin, setGeneratedPin] = useState(null);
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');
  const [registerToken, setRegisterToken] = useState('');
  const [validationSchema, setValidationSchema] = useState();
  const { search } = useLocation();

  useEffect(() => {
    setValidationSchema(createValidationSchema());
  }, []);

  useEffect(() => {
    if (token) {
      history.push('');
    }
  }, [token]);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    if (searchParams && searchParams.get('username')) setUsername(searchParams.get('username'));
    if (searchParams && searchParams.get('role')) setRole(searchParams.get('role'));
    if (searchParams && searchParams.get('token')) setRegisterToken(searchParams.get('token'));
  }, [search]);

  useEffect(() => {
    setValidationSchema(createValidationSchema());
  }, [role]);

  useEffect(() => {
    if (serverErrors) {
      setDisplayError(getErrorMessage(serverErrors));
    }
  }, [serverErrors]);

  const generatePin = () => {
    setGeneratedPin(`000${Math.floor(Math.random() * 10000)}`.slice(-4));
  };

  const submitHandler = (values) => {
    const payload = {
      first_name: values.first_name,
      last_name: values.last_name,
      username: values.email.toLowerCase(),
      phone_number: values.phone_number,
      email: values.email.toLowerCase(),
      password: values.password,
      role,
      token: registerToken,
    };
    if (values.qr) payload.pin = generatedPin;
    dispatch(register(payload));
  };

  const createValidationSchema = () => {
    return yup.object({
      first_name: yup.string().required('*Required'),
      last_name: yup.string().required('*Required'),
      email: yup.string().email('Enter a valid email').required('*Required'),
      phone_number:
        role === 'HCP'
          ? yup.string().matches(/^\+[1-9]\d{10,14}$/, 'Phone number must be in E164 format (ie +12223334444)')
          : yup
              .string()
              .matches(/^\+[1-9]\d{10,14}$/, 'Phone number must be in E164 format (ie +12223334444)')
              .required('*Required'),
      password: yup.string().required('*Required'),
      'password-validate': yup
        .string()
        .oneOf([yup.ref('password'), null], 'Passwords must match')
        .required('*Required'),
    });
  };

  return (
    <div className={classes.registerWrapper}>
      <CssBaseline />
      <Card className={classes.cardWrapper}>
        <CardHeader title="Register" />
        <CardContent>
          <Formik
            initialValues={{
              first_name: '',
              last_name: '',
              email: username,
              phone_number: '',
              password: '',
              'password-validate': '',
              qr: false,
              agree: false,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setTimeout(() => {
                submitHandler(values);
                setSubmitting(false);
              }, 400);
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => {
              return (
                <form className={classes.form} onSubmit={handleSubmit}>
                  <div className={classes.row}>
                    <div className={classes.nameFieldContainer}>
                      <TextField
                        classes={{ root: 'text-field' }}
                        fullWidth
                        label="First Name*"
                        placeholder="First Name*"
                        name="first_name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.first_name}
                      />
                      <p className={classes.error}>{errors.first_name && touched.first_name && errors.first_name}</p>
                    </div>
                    <div className={classes.nameFieldContainer}>
                      <TextField
                        classes={{ root: 'text-field' }}
                        fullWidth
                        placeholder="Last Name*"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.last_name}
                        name="last_name"
                        label="Last Name*"
                      />
                      <p className={classes.error}>{errors.last_name && touched.last_name && errors.last_name}</p>
                    </div>
                  </div>
                  <div className={classes.row}>
                    <div className={classes.nameFieldContainer}>
                      <TextField
                        classes={{ root: 'text-field' }}
                        fullWidth
                        placeholder="Email*"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        name="email"
                        label="Email*"
                      />
                      <p className={classes.error}>{errors.email && touched.email && errors.email}</p>
                    </div>
                    <div className={classes.nameFieldContainer}>
                      <TextField
                        classes={{ root: 'text-field' }}
                        fullWidth
                        placeholder={role === 'HCP' ? 'Phone Number' : 'Phone Number*'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.phone_number}
                        name="phone_number"
                        label={role === 'HCP' ? 'Phone Number' : 'Phone Number*'}
                      />
                      <p className={classes.error}>
                        {errors.phone_number && touched.phone_number && errors.phone_number}
                      </p>
                    </div>
                  </div>
                  <div className={classes.row}>
                    <div className={classes.nameFieldContainer}>
                      <TextField
                        classes={{ root: 'text-field' }}
                        fullWidth
                        placeholder="Password*"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                        name="password"
                        type="password"
                        label="Password*"
                      />
                      <p className={classes.error}>{errors.password && touched.password && errors.password}</p>
                    </div>
                    <div className={classes.nameFieldContainer}>
                      <TextField
                        classes={{ root: 'text-field' }}
                        fullWidth
                        placeholder="Re-Enter Password*"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values['password-validate']}
                        name="password-validate"
                        type="password"
                        label="Re-Enter Password*"
                      />
                      <p className={classes.error}>
                        {errors['password-validate'] && touched['password-validate'] && errors['password-validate']}
                      </p>
                    </div>
                  </div>
                  <div className={classes.row}>
                    <FormControlLabel
                      label="Enable App Login Via QR Code?"
                      control={(
                        <Switch
                          value={values.qr}
                          color="primary"
                          onChange={(_event, checked) => {
                            if (checked) generatePin();
                            setFieldValue('qr', checked);
                          }}
                        />
                      )}
                    />
                  </div>
                  {values.qr && (
                    <div className={classes.row}>
                      <div className={classes.pinContainer}>
                        <h2>
                          Your PIN:
                          {` ${generatedPin}`}
                        </h2>
                        <ReactToPrint
                          trigger={() => <Button variant="contained">Print QR Code</Button>}
                          content={() => printRef.current}
                        />
                      </div>
                      <div className={classes.nameFieldContainer}>
                        <QRCode
                          ref={printRef}
                          value={`neuralert://qrcodelaunch?baseUrl=${ROOT}&username=${values.email}`}
                        />
                      </div>
                    </div>
                  )}
                  {(role !== 'HCP' || values.phone_number) && (
                    <div className={classes.row}>
                      <FormControlLabel
                        label={(
                          <p className={classes.checkbox}>
                            I agree to receive text message alerts from Neuralert at the phone number provided above. I
                            understand data rates may apply, reply STOP to opt out
                          </p>
                        )}
                        control={(
                          <Checkbox
                            value={values.agree}
                            color="primary"
                            onChange={(_event, checked) => {
                              setFieldValue('agree', checked);
                            }}
                          />
                        )}
                      />
                    </div>
                  )}
                  {displayError &&
                    displayError.map((error) => (
                      <div key={error} style={{ color: 'red' }}>
                        {error}
                      </div>
                    ))}
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !(values.phone_number.length || values.qr) ||
                      !(!values.phone_number.length || values.agree)
                    }
                    className={classes.registerButton}
                  >
                    Register
                  </Button>
                </form>
              );
            }}
          </Formik>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
